import React, { useRef, useState, useEffect } from 'react'
import { useReactToPrint } from 'react-to-print';
import { useParams } from 'react-router-dom';
import { useAlert } from '../../../../../../context/AlertContext';
import voucherServices from '../voucherServices';
import companyServices from '../../company/companyServices';
import { useAppData } from '../../../../../../context/AppDataContext';
import { CancelOutlined, CheckCircle, Email, Phone, Print } from '@mui/icons-material';
import { NumberToWords } from '../section/NumberToWords';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';

// Import the specific forms we created
import ContraVoucherForm from '../component/ContraVoucherForm';
import JournalVoucherForm from '../component/JournalVoucherForm';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';

function ContraJournalVoucherDetailsPage() {
    const { id } = useParams();
    const printComponentRef = useRef();
    const { AppAlert } = useAlert();
    const { getMasterLabel, ledgerDD, getDDDataById } = useAppData();
    
    const [dialogStatus, setDialogStatus] = useState(false);
    const [voucherDetails, setVoucherDetails] = useState(null);
    const [companyDetails, setCompanyDetails] = useState(null);

    const getVoucherDetails = () => {
        voucherServices.getById(id).then(res => {
            setVoucherDetails(res.data);
        }).catch(error => console.log(error));
    }

    const getCompanyDetails = (companyId) => {
        companyServices.getById(companyId).then(res => {
            setCompanyDetails(res.data);
        }).catch(error => console.log(error));
    }

    useEffect(() => {
        getVoucherDetails();
    }, [id]);

    useEffect(() => {
        if (voucherDetails && !companyDetails) {
            getCompanyDetails(voucherDetails.companyId);
        }
    }, [voucherDetails]);

    const handlePrint = useReactToPrint({
        content: () => printComponentRef.current,
        pageStyle: `@page { size: auto; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; } }`
    });

    const finalize = () => {
        setDialogStatus(true);
        // Using the same financial finalize logic as Payment/Receipt
        voucherServices.contraJournalFinalize(id).then(res => {
            AppAlert(res.msg);
            getVoucherDetails();
        }).catch(error => AppAlert(error.msg, "error")).finally(() => setDialogStatus(false));
    }

    const discard = () => {
        setDialogStatus(true);
        voucherServices.contraJournalDiscard(id).then(res => {
            AppAlert(res.msg);
            getVoucherDetails();
        }).catch(error => AppAlert(error.msg, "error")).finally(() => setDialogStatus(false));
    }

    // Helper to get labels from ledgerDD
    const getLedgerLabel = (ledgerId) => getDDDataById(ledgerDD, ledgerId)?.label || 'N/A';

    return (
        <FinanceLayout>
            {voucherDetails && companyDetails &&
                <div className='container'>
                    <Dialog open={dialogStatus} fullWidth maxWidth="sm">
                        <DialogContent className='text-center'>
                            <CircularProgress /><div className='mt-2'>Processing Voucher...</div>
                        </DialogContent>
                    </Dialog>

                    {/* ACTION BUTTONS SECTION */}
                    <div className='row mb-2 no-print'>
                        <div className='col-md-12'>
                            {voucherDetails.status === 1 && (
                                <>
                                    {voucherDetails.voucherType === 'Contra' ? 
                                        <ContraVoucherForm data={voucherDetails} callBack={getVoucherDetails} /> : 
                                        <JournalVoucherForm data={voucherDetails} callBack={getVoucherDetails} />
                                    }
                                    <button className='btn btn-outline-primary float-end ms-2' onClick={finalize}><CheckCircle /> Finalize</button>
                                </>
                            )}
                            
                            {voucherDetails.status === 2 && (
                                <>
                                    <button className='btn btn-success ms-2' disabled>Finalized <CheckCircle /></button>
                                    <button className='btn btn-outline-danger float-end ms-2' onClick={discard}><CancelOutlined /> Discard</button>
                                </>
                            )}

                            {voucherDetails.status === 3 && <button className='btn btn-danger ms-2'>Discarded <CancelOutlined /></button>}
                            
                            <button className='btn btn-outline-primary float-end ms-2' onClick={() => setTimeout(handlePrint, 100)}><Print /> Print</button>
                        </div>
                    </div>

                    {/* PRINTABLE VOUCHER FORMAT */}
                    <div ref={printComponentRef} className="p-3 border">
                        <div className='row'>
                            <div className='col-md-12 text-center'>
                                <div className='fw-bold' style={{ fontSize: 16 }}>{companyDetails.companyName}</div>
                                <div style={{ fontSize: 12 }}>{companyDetails.address}, {companyDetails.city}, {getMasterLabel("indian-state", companyDetails.state)} - {companyDetails.zip}</div>
                                <div className='small text-muted'>GSTIN: {companyDetails.gst.toUpperCase()} | Email: {companyDetails.email}</div>
                                <h4 className='fw-bold mt-3 text-uppercase' style={{ letterSpacing: '1px' }}>{voucherDetails.voucherType} Voucher</h4>
                            </div>
                        </div>

                        <div className='row mt-3'>
                            <div className='col-6'>Voucher No: <span className='fw-bold'>{voucherDetails.voucherNo}</span></div>
                            <div className='col-6 text-end'>Date: <span className='fw-bold'>{voucherDetails.voucherDate}</span></div>
                        </div>

                        <div className='row mt-2'>
                            <div className='col-md-12'>
                                <table className='table table-bordered mb-0' style={{ fontSize: 13 }}>
                                    <thead>
                                        <tr className='bg-light'>
                                            <th>Particulars</th>
                                            <th width="25%" className='text-end'>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* DEBIT SIDE */}
                                        <tr style={{ minHeight: '100px' }}>
                                            <td>
                                                <div className='fw-bold text-primary'>Account (Debit / Receiver):</div>
                                                <div className='ms-4 mt-1' style={{ fontSize: 15 }}>{getLedgerLabel(voucherDetails.ledgerId)}</div>
                                            </td>
                                            <td className='text-end align-middle'>
                                                <span style={{ fontSize: 16 }} className='fw-bold'>
                                                    {Number(voucherDetails.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                            </td>
                                        </tr>
                                        {/* CREDIT SIDE */}
                                        <tr>
                                            <td colSpan="2">
                                                <div className='fw-bold text-danger'>Through (Credit / Giver):</div>
                                                <div className='ms-4 mt-1' style={{ fontSize: 15 }}>{getLedgerLabel(voucherDetails.paymentLedgerId)}</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        {voucherDetails.instrumentNo && (
                                            <tr>
                                                <td colSpan="2"><strong>Ref/Instrument:</strong> {voucherDetails.instrumentNo} (Date: {voucherDetails.instrumentDate})</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td colSpan="2"><strong>Narration:</strong> {voucherDetails.narration || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Amount in words:</strong> <br />
                                                <i className='ms-2'><NumberToWords amount={voucherDetails.grandTotal} /> Only</i>
                                            </td>
                                            <td className='text-end align-middle bg-light'>
                                                <div className='fw-bold' style={{ fontSize: 16 }}>
                                                    ₹ {Number(voucherDetails.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className='row mt-5 pt-4'>
                            <div className='col-6 text-center'>
                                <div className='border-top d-inline-block px-5'>Receiver's Signature</div>
                            </div>
                            <div className='col-6 text-center'>
                                <div className='border-top d-inline-block px-5'>Authorised Signatory</div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </FinanceLayout>
    )
}

export default ContraJournalVoucherDetailsPage